import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios/index';
import { serialize } from 'object-to-formdata';
import { IGetListParams, IPostParams } from '../types/api';
import { useSnackbar } from 'notistack';
import axiosInstance from 'src/utils/axios';

const useApiServices = () => {
  const { enqueueSnackbar } = useSnackbar();

  const useGetListApi = <T,>({ parameters, url, onSuccess, onError, enabled }: IGetListParams) => {
    return useQuery<T, AxiosError>(
      [url, parameters],
      async () => await axiosInstance.get(url, { params: parameters }),
      {
        onSuccess: onSuccess,
        onError: onError,
        enabled,
      }
    );
  };
  // Post service
  const usePostApi = <T,>({
    url,
    urlAfterSuccess,
    onSuccess,
    onError,
    withSuccessNotistack = true,
    withErrorNotistack = true,
    withFormData = false,
  }: IPostParams) => {
    const queryClient = useQueryClient();
    return useMutation<T, AxiosError, T>(
      (requestData) => {
        return axiosInstance.post(
          url,
          withFormData ? serialize(requestData, {}, undefined) : requestData
        );
      },
      {
        onSuccess: (data) => {
          queryClient.invalidateQueries([urlAfterSuccess ?? url]);
          withSuccessNotistack && handleApiSuccesswithNotistack();
          onSuccess && onSuccess(data);
        },
        onError: (error) => {
          withErrorNotistack && handleApiErrorwithNotistack(error);
          onError && onError(error);
        },
      }
    );
  };

  // For show snackbar when api return a error
  const handleApiErrorwithNotistack = (error: any) => {
    if (error?.errors) {
      error?.errors.forEach((error: any) => {
        enqueueSnackbar({
          variant: 'error',
          message: error,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      });
    } else {
      enqueueSnackbar({
        variant: 'error',
        message: error?.message ?? error?.error,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }
  };

  // For show snackbar when api successfully
  const handleApiSuccesswithNotistack = () => {
    enqueueSnackbar({
      variant: 'success',
      message: 'Completed successfully',
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
    });
  };

  return {
    usePostApi,
    useGetListApi,
  };
};

export default useApiServices;
